import { Component, Input, OnInit } from '@angular/core'
import { ConnectionView, isApiConnectionActive } from '@tv3/store/connection/connection.view'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { Channel } from '@tokeet-frontend/tv3-platform'
import { ApiConnectionStatus } from '@tv3/store/connection/connection.types'
import { catchError, exhaustMap, filter, take, takeUntil } from 'rxjs/operators'
import { Actions, ofType } from '@ngrx/effects'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'
import {
  ImportBookingsFromConnectionComplete,
  GetConnectionStatuesComplete,
  PushAvailabilityToConnectionComplete,
  PushRatesToConnectionComplete,
} from '@tv3/store/connection/connection.actions'
import { get, max } from 'lodash'
import { Store } from '@ngrx/store'
import { of, timer } from 'rxjs'

type SyncStatusField = 'lastavailpull' | 'lastavailpush' | 'lastratepush'

const syncStatusPollInterval = 5 * 1000
const syncStatusPollTimeout = 2 * 60 * 1000

@Component({
  selector: 'app-api-connection-status',
  templateUrl: './api-connection-status.component.html',
  styleUrls: ['./api-connection-status.component.scss'],
})
export class ApiConnectionStatusComponent extends Destroyable implements OnInit {
  @Input() channel: Channel
  @Input() connection: ConnectionView
  @Input() dateFormat: string

  @Input() availabilityPull: boolean = true
  @Input() availabilityPush: boolean = true
  @Input() ratesPush: boolean = true

  connectionStatus: ApiConnectionStatus

  private pendingSyncFields = new Set<SyncStatusField>()
  private syncPollGeneration: Record<SyncStatusField, number> = {
    lastavailpull: 0,
    lastavailpush: 0,
    lastratepush: 0,
  }

  constructor(private actions: Actions, private store: Store<any>, private connectionService: ConnectionService) {
    super()
  }

  ngOnInit() {
    this.refreshStatus()
    this.listenDataChange()
  }

  listenDataChange() {
    this.actions
      .pipe(
        ofType(ImportBookingsFromConnectionComplete),
        filter(({ update }) => update.id === this.connection.id),
        untilDestroy(this)
      )
      .subscribe(({ update }) => {
        if (update.changes.lastavailpull) {
          this.connectionStatus.lastavailpull = update.changes.lastavailpull
          this.setStatusText(this.connectionStatus)
        } else {
          this.onSyncScheduled('lastavailpull')
        }
      })

    this.actions
      .pipe(
        ofType(PushAvailabilityToConnectionComplete),
        filter(({ update }) => update.id === this.connection.id),
        untilDestroy(this)
      )
      .subscribe(({ update }) => {
        if (update.changes.lastavailpush) {
          this.connectionStatus.lastavailpush = update.changes.lastavailpush
          this.setStatusText(this.connectionStatus)
        } else {
          this.onSyncScheduled('lastavailpush')
        }
      })

    this.actions
      .pipe(
        ofType(PushRatesToConnectionComplete),
        filter(({ update }) => update.id === this.connection.id),
        untilDestroy(this)
      )
      .subscribe(({ update }) => {
        if (update.changes.lastratepush) {
          this.connectionStatus.lastratepush = update.changes.lastratepush
          this.setStatusText(this.connectionStatus)
        } else {
          this.onSyncScheduled('lastratepush')
        }
      })
  }

  private onSyncScheduled(field: SyncStatusField) {
    const previousStamp = max([this.connectionStatus?.[field], this.connection[field]]) || 0
    const generation = ++this.syncPollGeneration[field]

    this.connectionStatus = this.connectionStatus || {}
    this.pendingSyncFields.add(field)
    this.connectionStatus.status = 'In Progress'

    let completed = false
    timer(0, syncStatusPollInterval)
      .pipe(
        exhaustMap(() => this.getStatus()),
        filter((status): status is ApiConnectionStatus => !!status && (status[field] || 0) > previousStamp),
        take(1),
        takeUntil(timer(syncStatusPollTimeout)),
        untilDestroy(this)
      )
      .subscribe({
        next: (status) => {
          if (generation !== this.syncPollGeneration[field]) return

          completed = true
          this.pendingSyncFields.delete(field)
          this.applyStatus(status)
        },
        complete: () => {
          if (completed || generation !== this.syncPollGeneration[field]) return

          this.pendingSyncFields.delete(field)
          this.connectionStatus.status = this.pendingSyncFields.size ? 'In Progress' : 'Pending'
        },
      })
  }

  refreshStatus() {
    this.getStatus()
      .pipe(take(1))
      .subscribe((res) => {
        this.applyStatus(res || {})
      })
  }

  private getStatus() {
    return this.connectionService
      .getApiConnectionStatus(this.channel.name, this.connection.propertyId, this.connection.roomId)
      .pipe(catchError(() => of(null)))
  }

  private applyStatus(response: ApiConnectionStatus) {
    const currentStatus = this.connectionStatus || {}
    const refreshedStamps = {
      lastavailpull: max([response.lastavailpull, currentStatus.lastavailpull, this.connection.lastavailpull]),
      lastavailpush: max([response.lastavailpush, currentStatus.lastavailpush, this.connection.lastavailpush]),
      lastratepush: max([response.lastratepush, currentStatus.lastratepush, this.connection.lastratepush]),
    }

    this.connectionStatus = { ...response, ...refreshedStamps }
    this.connectionStatus.lastratepush = max([
      this.connectionStatus.lastratepush,
      get(this.connection.rental, 'attributes.last_rateengine_update'),
    ])

    if (response.lastavailpull || response.lastavailpush || response.lastratepush) {
      this.store.dispatch(
        GetConnectionStatuesComplete({
          updates: [{ id: this.connection.id, changes: refreshedStamps }],
        })
      )
    }

    if (this.pendingSyncFields.size) {
      this.connectionStatus.status = 'In Progress'
    } else {
      this.setStatusText(this.connectionStatus)
    }
  }

  private setStatusText(statusObj: ApiConnectionStatus) {
    // tslint:disable-next-line:triple-equals
    if (this.connection.status == 0) {
      statusObj.status = 'Disabled'
    } else if (statusObj.loginfail) {
      statusObj.status = 'Login Failed'
    } else if (isApiConnectionActive(this.connection, statusObj)) {
      statusObj.status = 'Active'
    } else {
      statusObj.status = 'Pending'
    }
  }
}
